/**
 * Diver sign-in: magic link by e-mail (plain fetch against better-auth) and passkeys (the
 * better-auth passkey client, loaded on demand so the map never pays for it).
 */
import { ApiRequestError } from "../api/errors.ts";

export type AuthFailure = "rate_limited" | "banned" | "network" | "failed";

const failure = (status: number, text: string): AuthFailure => {
  if (status === 429) return "rate_limited";
  if (/banned/i.test(text)) return "banned";
  return "failed";
};

async function post(path: string, body: unknown): Promise<Response> {
  try {
    return await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiRequestError("network", "network error");
  }
}

/** Where the link leads after it is used: the account page (it offers the passkey on a first visit). */
export interface MagicLinkTargets {
  callbackURL: string;
  errorCallbackURL: string;
}

export function magicLinkTargets(
  origin: string,
  locale: string,
  next?: string | null,
): MagicLinkTargets {
  return {
    // where the visitor was going (a path on this site), else the account page, which offers a passkey
    callbackURL: next ? `${origin}${next}` : `${origin}/${locale}/konto?welcome=1`,
    errorCallbackURL: `${origin}/${locale}/login`,
  };
}

/** Asks for the sign-in link. Always "sent" for any address (the server never reveals whether one exists). */
export async function requestMagicLink(
  email: string,
  name: string | undefined,
  targets: MagicLinkTargets,
): Promise<{ ok: true } | { ok: false; reason: AuthFailure }> {
  let res: Response;
  try {
    res = await post("/api/auth/sign-in/magic-link", {
      email,
      ...(name ? { name } : {}),
      ...targets,
    });
  } catch {
    return { ok: false, reason: "network" };
  }
  if (res.ok) return { ok: true };
  return { ok: false, reason: failure(res.status, await res.text().catch(() => "")) };
}

// ------------------------------------------------------------------ passkeys

export interface PasskeyInfo {
  id: string;
  name: string | null;
  createdAt: string | null;
}

async function client() {
  const [{ createAuthClient }, { passkeyClient }] = await Promise.all([
    import("better-auth/client"),
    import("@better-auth/passkey/client"),
  ]);
  return createAuthClient({ basePath: "/api/auth", plugins: [passkeyClient()] });
}

export const passkeysSupported = (): boolean =>
  typeof window !== "undefined" && "PublicKeyCredential" in window;

export async function signInWithPasskey(): Promise<{ ok: boolean; reason?: AuthFailure }> {
  const res = await (await client()).signIn.passkey();
  if (res?.error) {
    return { ok: false, reason: failure(res.error.status ?? 0, res.error.message ?? "") };
  }
  return { ok: true };
}

export async function addPasskey(name: string): Promise<{ ok: boolean; cancelled?: boolean }> {
  const res = await (await client()).passkey.addPasskey({ name });
  if (res?.error) {
    const code = String((res.error as { code?: string }).code ?? "");
    return { ok: false, cancelled: /CANCEL|ABORT|NOT_ALLOWED/i.test(code) };
  }
  return { ok: true };
}

export async function listPasskeys(): Promise<PasskeyInfo[]> {
  const res = await (await client()).passkey.listUserPasskeys();
  const rows = (res?.data ?? []) as {
    id: string;
    name?: string | null;
    createdAt?: string | Date | null;
  }[];
  return rows.map((p) => ({
    id: p.id,
    name: p.name ?? null,
    createdAt: p.createdAt ? new Date(p.createdAt).toISOString() : null,
  }));
}

export async function deletePasskey(id: string): Promise<boolean> {
  const res = await (await client()).passkey.deletePasskey({ id });
  return !res?.error;
}
